import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatDate } from '@/lib/utils';
import { createAccountOpportunity } from '../api';
import { FileText, Clock, AlertTriangle, CheckCircle, Plus } from 'lucide-react';
import type { AccountSubscription } from '../types';

interface SubscriptionRenewalCardProps {
  accountId: string;
  contract: AccountSubscription | undefined;
  hasRenewalOpportunity: boolean;
  onNavigateToTab: (tab: string) => void;
}

export default function SubscriptionRenewalCard({
  accountId,
  contract,
  hasRenewalOpportunity,
  onNavigateToTab,
}: SubscriptionRenewalCardProps) {
  const queryClient = useQueryClient();

  const createRenewalMutation = useMutation({
    mutationFn: () =>
      createAccountOpportunity(accountId, {
        title: `Subscription Renewal - ${contract?.contract_number || 'Current Subscription'}`,
        type: 'renewal',
        estimatedValue: contract?.total_value,
        targetDate: contract?.end_date,
      }),
    onSuccess: () => {
      // Invalidate dashboard to refresh hasRenewalOpportunity
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      // Navigate to opportunities tab
      onNavigateToTab('opportunities');
    },
  });

  // Calculate days until expiry
  const daysUntilExpiry = contract?.end_date
    ? Math.ceil((new Date(contract.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Current Subscription
            </CardTitle>
            <CardDescription>Active subscription details</CardDescription>
          </div>
          {daysUntilExpiry !== null && <ExpiryBadge days={daysUntilExpiry} />}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {contract ? (
          <>
            {/* Contract Details */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Number</span>
                <span className="font-medium">{contract.contract_number}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Value</span>
                <span className="font-medium">
                  {formatCurrency(contract.total_value, contract.currency)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">End Date</span>
                <span className="font-medium">{formatDate(contract.end_date)}</span>
              </div>
            </div>

            {/* Expiry Urgency Display */}
            {daysUntilExpiry !== null && daysUntilExpiry <= 90 && (
              <div className={`flex items-center gap-2 p-2 rounded-md ${getUrgencyBgColor(daysUntilExpiry)}`}>
                {getUrgencyIcon(daysUntilExpiry)}
                <span className="text-sm font-medium">
                  {daysUntilExpiry <= 0
                    ? 'Subscription has expired'
                    : `${daysUntilExpiry} days until expiry`}
                </span>
              </div>
            )}

            {/* Renewal Status & Action */}
            <div className="pt-2 border-t">
              {hasRenewalOpportunity ? (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span>Renewal opportunity exists</span>
                  <Button
                    variant="link"
                    size="sm"
                    className="ml-auto p-0 h-auto text-primary"
                    onClick={() => onNavigateToTab('opportunities')}
                  >
                    View &rarr;
                  </Button>
                </div>
              ) : daysUntilExpiry !== null && daysUntilExpiry <= 90 ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => createRenewalMutation.mutate()}
                  disabled={createRenewalMutation.isPending}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {createRenewalMutation.isPending
                    ? 'Creating...'
                    : 'Create Renewal Opportunity'}
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground text-center">
                  Renewal action available when subscription expires within 90 days
                </p>
              )}
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">No active subscription</p>
        )}
      </CardContent>
    </Card>
  );
}

function ExpiryBadge({ days }: { days: number }) {
  if (days <= 0) {
    return <Badge variant="destructive">Expired</Badge>;
  }
  if (days <= 30) {
    return <Badge variant="destructive">{days}d</Badge>;
  }
  if (days <= 60) {
    return <Badge variant="warning" className="bg-orange-100 text-orange-700">{days}d</Badge>;
  }
  if (days <= 90) {
    return <Badge variant="outline" className="border-yellow-400 text-yellow-700">{days}d</Badge>;
  }
  return <Badge variant="outline" className="border-green-400 text-green-700">{days}d</Badge>;
}

function getUrgencyBgColor(days: number): string {
  if (days <= 0) return 'bg-red-100 text-red-800';
  if (days <= 30) return 'bg-red-50 text-red-700';
  if (days <= 60) return 'bg-orange-50 text-orange-700';
  return 'bg-yellow-50 text-yellow-700';
}

function getUrgencyIcon(days: number) {
  if (days <= 30) {
    return <AlertTriangle className="h-4 w-4 text-red-600" />;
  }
  return <Clock className="h-4 w-4" />;
}
